/**
 * Respondent filters layered on top of an audience: geography, age band and
 * urbanicity. Each filter shifts the KPIs by a small, deterministic amount and
 * reduces the effective sample. Demo only.
 */
import { STATES } from "./regions";
import {
  AgeBand,
  Configuration,
  Filters,
  KpiId,
  Kpis,
  Region,
  Urbanicity,
} from "./types";
import { SAMPLE_SIZE } from "./voice";

export const AGE_BANDS: { label: string; value: AgeBand }[] = [
  { label: "All ages", value: "all" },
  { label: "18–34", value: "18-34" },
  { label: "35–54", value: "35-54" },
  { label: "55+", value: "55+" },
];

export const DEFAULT_FILTERS: Filters = {
  age: "all",
  region: "all",
  urbanicity: "all",
};

export const REGIONS: { label: string; value: Region }[] = [
  { label: "All regions", value: "all" },
  { label: "Northeast", value: "northeast" },
  { label: "Midwest", value: "midwest" },
  { label: "South", value: "south" },
  { label: "West", value: "west" },
];

export const URBANICITIES: { label: string; value: Urbanicity }[] = [
  { label: "All areas", value: "all" },
  { label: "Urban", value: "urban" },
  { label: "Suburban", value: "suburban" },
  { label: "Rural", value: "rural" },
];

/* ── Public API ─────────────────────────────────── */

/** KPIs after applying the filters to an audience-level result. */
export const applyFilters = (
  kpis: Kpis,
  configuration: Configuration,
  filters: Filters
): Kpis => {
  const out = { ...kpis };
  const shifts = [
    ...ageShift(filters.age, configuration),
    ...urbanShift(filters.urbanicity, configuration),
    ...regionShift(filters.region),
  ];
  for (const [kpi, delta] of shifts) out[kpi] += delta;
  out.purchaseIntent = clamp(out.purchaseIntent, 3, 97);
  out.appeal = clamp(out.appeal, 5, 99);
  out.brandFit = clamp(out.brandFit, 5, 99);
  out.awareness = clamp(out.awareness, 2, 95);
  out.trial = clamp(out.trial, 1, 80);
  out.wtp = Math.max(3.5, out.wtp);
  return out;
};

/** Human-readable summary of active filters, or null when none. */
export const describeFilters = (filters: Filters): string | null => {
  const parts = [
    filters.region !== "all" &&
      REGIONS.find((r) => r.value === filters.region)?.label,
    filters.age !== "all" && `aged ${filters.age}`,
    filters.urbanicity !== "all" &&
      URBANICITIES.find(
        (u) => u.value === filters.urbanicity
      )?.label.toLowerCase(),
  ].filter((p): p is string => typeof p === "string");
  return parts.length > 0 ? parts.join(" · ") : null;
};

/** Effective respondent count once filters are applied. */
export const filteredSampleSize = (filters: Filters): number => {
  const share =
    REGION_SHARE[filters.region] *
    AGE_SHARE[filters.age] *
    URBAN_SHARE[filters.urbanicity];
  return Math.max(30, Math.round(SAMPLE_SIZE * share));
};

export const isFiltered = (filters: Filters): boolean =>
  filters.age !== "all" ||
  filters.region !== "all" ||
  filters.urbanicity !== "all";

/** States covered by the geography filter. */
export const statesInRegion = (region: Region): Set<string> =>
  new Set(
    STATES.filter((s) => region === "all" || s.region === region).map(
      (s) => s.id
    )
  );

/* ── Private ────────────────────────────────────── */

type Shift = [KpiId, number];

const AGE_SHARE: Record<AgeBand, number> = {
  "18-34": 0.31,
  "35-54": 0.36,
  "55+": 0.33,
  all: 1,
};

const REGION_SHARE: Record<Region, number> = {
  all: 1,
  midwest: 0.21,
  northeast: 0.17,
  south: 0.38,
  west: 0.24,
};

const URBAN_SHARE: Record<Urbanicity, number> = {
  all: 1,
  rural: 0.19,
  suburban: 0.52,
  urban: 0.29,
};

const ageShift = (age: AgeBand, c: Configuration): Shift[] => {
  switch (age) {
    case "18-34":
      return [
        ["purchaseIntent", 2 + (c.positioning === "protein" ? 3 : 0)],
        ["appeal", 2],
        ["awareness", c.advertising === "influencer" ? 5 : 1],
        ["brandFit", -3],
        ["wtp", -0.18],
        ["trial", 1],
      ];
    case "35-54":
      return [
        ["purchaseIntent", 1 + (c.positioning === "family" ? 2 : 0)],
        ["appeal", 1],
        ["awareness", c.advertising === "retailMedia" ? 2 : 0],
        ["wtp", 0.08],
        ["trial", 0.5],
      ];
    case "55+":
      return [
        ["purchaseIntent", -4 - (c.positioning === "protein" ? 2 : 0)],
        ["appeal", -4],
        [
          "awareness",
          c.advertising === "tv" || c.advertising === "tvSocial" ? 3 : -3,
        ],
        ["brandFit", 3],
        ["wtp", 0.05],
        ["trial", -1.5],
      ];
    default:
      return [];
  }
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const regionShift = (region: Region): Shift[] => {
  switch (region) {
    case "northeast":
      return [
        ["purchaseIntent", 1.5],
        ["brandFit", 2],
        ["wtp", 0.12],
      ];
    case "midwest":
      return [
        ["purchaseIntent", -1],
        ["wtp", -0.08],
        ["trial", 0.5],
      ];
    case "south":
      return [
        ["purchaseIntent", 0.5],
        ["appeal", 1],
        ["wtp", -0.1],
      ];
    case "west":
      return [
        ["purchaseIntent", -1.5],
        ["awareness", 1],
        ["wtp", 0.06],
      ];
    default:
      return [];
  }
};

const urbanShift = (urbanicity: Urbanicity, c: Configuration): Shift[] => {
  switch (urbanicity) {
    case "urban":
      return [
        ["purchaseIntent", 1.5 + (c.retail === "wholeFoods" ? 3 : 0)],
        ["awareness", 2],
        ["wtp", 0.15],
        ["trial", c.retail === "walmart" ? -1 : 0.5],
      ];
    case "suburban":
      return [
        ["purchaseIntent", 1],
        ["awareness", c.retail === "mass" ? 1 : 0],
        ["trial", 0.5],
      ];
    case "rural":
      return [
        [
          "purchaseIntent",
          -3 + (c.retail === "walmart" || c.retail === "mass" ? 2 : 0),
        ],
        ["appeal", -1],
        ["awareness", c.retail === "wholeFoods" ? -5 : -2],
        ["wtp", -0.2],
        ["trial", c.retail === "wholeFoods" ? -3 : -0.5],
      ];
    default:
      return [];
  }
};
